import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { VehicleSubscriptionQrcode } from '@euler/app/flows/order/pages/OrderPreviewScreen/components/VehicleSubscriptionQrcode';
import { Center } from '@euler/components/Center';
import { Img } from '@euler/components/image/AdvancedImage';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { delayed } from '@euler/utils';
import { useNavigation } from '@react-navigation/native';
import { AnimatePresence, MotiView } from 'moti';
import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { Easing } from 'react-native-reanimated';

type ImageInfo = { url: string; size: number };
const kDuration = 250;

export const QrcodeScanScreen = wrapNavigatorScreen(
  () => {
    const { rpx } = useResponsiveContext();
    const [image, setImage] = useState<ImageInfo>();
    const navigation = useNavigation();
    const [show, setShow] = useState(true);
    const {
      formController: { form },
      headerMode$,
    } = useOrderFlowContext();

    const onClose = useCallback(() => {
      setShow(false);
      delayed(() => {
        navigation.goBack();
      }, kDuration);
    }, [navigation]);

    const onLoad = useCallback((url: string, size: number) => {
      setImage({ url, size });
    }, []);

    useEffect(() => {
      headerMode$.next('screen');
      return () => {
        headerMode$.next('float');
      };
    }, [headerMode$]);

    const licensePlateNo = form.licensePlateNo.value;

    return (
      <View
        css={`
          flex: 1;
        `}
      >
        <AnimatePresence>
          {show && (
            <MotiView
              style={StyleSheet.absoluteFill}
              from={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                type: 'timing',
                duration: kDuration,
                easing: Easing.inOut(Easing.linear),
              }}
              exitTransition={{
                type: 'timing',
                duration: kDuration,
                easing: Easing.inOut(Easing.linear),
              }}
              css={`
                background-color: rgba(0, 0, 0, 0.75);
              `}
            >
              <TouchableWithoutFeedback onPress={onClose}>
                <View
                  style={StyleSheet.absoluteFill}
                  css={`
                    flex: 1;
                  `}
                />
              </TouchableWithoutFeedback>
              <VehicleSubscriptionQrcode
                licensePlateNo={licensePlateNo}
                onLoad={onLoad}
                css={`
                  position: absolute;
                  opacity: 0;
                `}
              />
            </MotiView>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {image != null && show && (
            <MotiView
              pointerEvents="box-none"
              transition={{
                type: 'timing',
                duration: kDuration,
                easing: Easing.inOut(Easing.linear),
              }}
              exitTransition={{
                type: 'timing',
                duration: kDuration,
                easing: Easing.inOut(Easing.linear),
              }}
              from={{
                opacity: 0,
                translateY: rpx(100),
              }}
              animate={{
                opacity: 1,
                translateY: 0,
              }}
              exit={{ opacity: 0, translateY: rpx(100) }}
              css={`
                flex: 1;
                justify-content: center;
                align-items: center;
              `}
            >
              <View
                css={`
                  padding: ${rpx(28)}px;
                  background-color: #fff;
                  border-radius: ${rpx(20)}px;
                `}
              >
                <Img
                  uri={image.url}
                  css={`
                    width: ${image.size}px;
                    height: ${image.size}px;
                  `}
                />
              </View>
            </MotiView>
          )}
        </AnimatePresence>
        {!image && show && (
          <Center
            css={`
              flex: 1;
            `}
          >
            <ActivityIndicator />
          </Center>
        )}
      </View>
    );
  },
  {
    presentation: 'transparentModal',
    headerShown: false,
    animationEnabled: false,
    cardStyle: { backgroundColor: 'transparent' },
  },
);
